import axios from '@/lib/axios'
import { buildAdminRequestConfig, type OrganizationId } from '@/lib/organization-scoping'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type {
  Category,
  CategoryIdInput,
  CategoryIndexFilters,
  CreateCategoryInput,
  PosCategoryOrderInput,
  UpdateCategoryInput,
} from './categories.types'

export interface CategoryOrganizationScope {
  organizationId: OrganizationId
}

export interface CategoryIndexQueryKeyEntries extends CategoryOrganizationScope {
  filters?: CategoryIndexFilters | undefined
}

export interface CategoryShowQueryKeyEntries extends CategoryOrganizationScope {
  categoryId?: CategoryIdInput | undefined
}

type CategoryQueryContext<TKey> = Pick<QueryFunctionContext<[TKey]>, 'queryKey'>

export async function getCategories({
  queryKey: [{ organizationId, filters = {} }],
}: CategoryQueryContext<CategoryIndexQueryKeyEntries>) {
  return (
    (await axios.get<Category[]>(
      '/categories',
      buildAdminRequestConfig({ organizationId, params: { ...filters } })
    )) || []
  )
}

export async function getCategory({
  queryKey: [{ organizationId, categoryId }],
}: CategoryQueryContext<CategoryShowQueryKeyEntries>) {
  return (
    (await axios.get<Category>(
      `/categories/${categoryId}`,
      buildAdminRequestConfig({ organizationId })
    )) || {}
  )
}

export async function createCategory({
  organizationId,
  ...values
}: CreateCategoryInput & CategoryOrganizationScope) {
  return axios.post<Category>('/categories', values, buildAdminRequestConfig({ organizationId }))
}

export async function udpateCategory({
  organizationId,
  id,
  ...values
}: UpdateCategoryInput & CategoryOrganizationScope & { id: CategoryIdInput }) {
  return axios.put<Category>(
    `/categories/${id}`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

/** Resolves to the reordered categories in POS order. */
export async function updatePosCategoryOrder({
  organizationId,
  categories,
}: CategoryOrganizationScope & { categories: PosCategoryOrderInput[] }) {
  return axios.put<Category[]>(
    '/categories/pos-order',
    { categories },
    buildAdminRequestConfig({ organizationId })
  )
}

/** 204 No Content: resolves to the empty body. */
export async function deleteCategory({
  organizationId,
  categoryId,
}: CategoryOrganizationScope & { categoryId: CategoryIdInput }) {
  return axios.delete<string>(
    `/categories/${categoryId}`,
    buildAdminRequestConfig({ organizationId })
  )
}
